import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';

const QUICK_ACTIONS_MARGIN = 16;
const QUICK_ACTIONS_SIDE_GUTTER = 52;
const SHARE_INITIALS_COLORS = ['#8EC8E8', '#2551BF', '#3D9B6E', '#F5A623', '#6B7CFF'];

@Component({
  selector: 'app-to-page-body',
  templateUrl: './to-page-body.component.html',
  styleUrls: ['./to-page-body.component.scss']
})
export class ToPageBodyComponent implements OnInit, OnDestroy {
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showShareViewModal) {
      this.closeShareViewModal();
    }
    if (this.highlightedTeam && !this.confirmationModalOpen) {
      this.highlightedTeam.showQuickActions = false;
      this.teardownQuickActionsObserver();
    }
    if (this.openFilterByScore) {
      this.openFilterByScore = false;
    }
    if (this.openFilterByTeams) {
      this.openFilterByTeams = false;
      this.teamToSearch = null;
      this.searchingTeam.next('');
    }
    if (this.selectedView && this.selectedView.openSettings) {
      this.selectedView.openSettings = false;
      this.memberToSearch = null;
      this.searchingMember.next('');
      this.viewMenuPosition = null;
    }
    if (this.showDeleteViewConfirmation) {
      this.showDeleteViewConfirmation = false;
      this.viewToDelete = null;
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    this.onViewsDragMove(event);
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp() {
    this.onViewsDragEnd();
  }

  @ViewChildren('teamQuickActions') teamQuickActions!: QueryList<ElementRef>;
  @ViewChild('viewSettingsMenu') viewSettingsMenu!: ElementRef<HTMLElement>;
  @ViewChild('viewsMainContainer') viewsMainContainer!: ElementRef<HTMLElement>;

  @Input() set teamsForOverview(value:any) {
    if (value && (value?.availableTeams.length && value?.backupTeams.length && value?.currentTeams.length)) {
      this.teams = value.currentTeams;
      this.defaultTeams = value.backupTeams;
      this.availableTeams = value?.availableTeams;
      this.getUserRoles();
    }
  }
  @Input() set aggregatedScoresSetter(value: any) {
    if (value) {
      this.aggregatedScores = value;
      this.aggregatedHasValue = true;
    }
  }
  @Input() set indicatorToShow(value: string) {
    this.indicatorForSort = value;
    this.openFilterByScore = false;
    this.openAggregated = false;
    this.openAggregatedOnce = false;
  }
  @Input() set userViewFilters(value: any) {
    if (value) {
      this.views = value;
    }
  }
  @Input() set membersToShareView(value: any) {
    if (value) {
       this.memberSearchedToShareView = (Array.isArray(value) ? value : []).map((item: any) =>
         item?.user ? item : { user: item }
       );
       this.showMembersLoading = false;
    }
  }
  @Input() set setSelectedViewAfterChanges(value: any) {
    this.selectedView = value;
    this.saveViewButton = !!value && value.creatorPermission;
  }
  @Input() set changesToView(value: boolean) {
    this._changesToView = !!value;
    if (!value) {
      this.viewSavePending = false;
    }
  }
  get changesToView(): boolean {
    return this._changesToView;
  }

  get isSaveEnabled(): boolean {
    return this._changesToView && !this.viewSavePending;
  }

  @Output() quickActionsOpened = new EventEmitter<any>();
  @Output() goToTeamDashboard = new EventEmitter<any>();
  @Output() setFilterForScore = new EventEmitter<any>();
  @Output() teamFilterCheck = new EventEmitter<any>();
  @Output() getAggregatedScores = new EventEmitter<any>();
  @Output() pauseLoopEvent = new EventEmitter<any>();
  @Output() removeMemberEvent = new EventEmitter<any>();
  @Output() createNewViewEvent = new EventEmitter<any>();
  @Output() openViewEvent = new EventEmitter<any>();
  @Output() patchViewEvent = new EventEmitter<any>();
  @Output() deleteViewEvent = new EventEmitter<any>();
  @Output() searchMemberViewEvent = new EventEmitter<any>();
  @Output() shareViewWithMemberEvent = new EventEmitter<any>();
  @Output() removeMemberFromViewEvent = new EventEmitter<any>();
  @Output() saveViewChangesEvent = new EventEmitter<any>();

  searchingMember = new Subject<string>();
  searchingTeam = new Subject<string>();

  highlightedTeam: any;
  selectedRadioValue: any;
  selectedView: any | null = null;
  tempViewTitle: any | null = null;
  firstSelectedRadioValue: any;
  lastSelectedRadioValue: any;
  aggregatedScores: any | null = null;
  memberToDelete: any | null = null;
  memberToSearch: any | null = null;
  teamToSearch: any | null = null;
  memberToShareView: any | null = null;
  viewToDelete: any | null = null;
  viewConfirmationPopupPosition: any | null = null;
  selectAllTeams: any | null = null;
  teamsPlaceholder: any | null = null;
  scrollStrategy: any;

  indicatorForSort: string = '';

  private quickActionsObserver?: ResizeObserver;

  openFilterByScore: boolean = false;
  openFilterByTeams: boolean = false;
  openAggregated: boolean = false;
  openAggregatedOnce: boolean = false;
  aggregatedHasValue: boolean = false;
  loadedOnce: boolean = false;
  confirmationModalOpen: boolean = false;
  pauseLoopModalOpen: boolean = false;
  deleteMemberModalOpen: boolean = false;
  showMembersLoading: boolean = false;
  saveViewButton: boolean = false;
  private _changesToView = false;
  private viewSavePending = false;
  shareViewWithMemberConfirmation: boolean = false;
  showShareViewModal: boolean = false;
  showDeleteViewConfirmation: boolean = false;
  viewsDragging = false;
  viewMenuPosition: { top: string, left: string } | null = null;
  shareMenuPosition: { top: string, left: string } | null = null;
  private viewsDragMoved = false;
  private viewsDragStartX = 0;
  private viewsScrollStart = 0;

  teams: any = [];
  defaultTeams: any[] = [];
  availableTeams: any[] = [];
  views: any[] = [];
  memberSearchedToShareView: any[] = [];
  filterOptions: {name: string, checked: boolean}[] = [
    {name: this.translate.instant('team_overview_high'), checked: false},
    {name: this.translate.instant('team_overview_low'), checked: false}
  ];
  radioButtons: any[] = [
    {label: this.translate.instant('team_overview_high'),slug: 'highest', id: 'radio1', value: 'DESC'},
    {label: this.translate.instant('team_overview_low'),slug: 'lowest', id: 'radio2', value: 'ASC'}];

  constructor(public data: DataManipulationService, private translate: TranslateService, private cdr: ChangeDetectorRef) {
    this.searchingMember.pipe(
        debounceTime(800),
        distinctUntilChanged()).subscribe((value: any) => {
          this.memberSearchedToShareView = [];
          this.showMembersLoading = true;
          if (value !== '') {
            this.searchMemberViewEvent.emit(value);
          }
        });
    this.searchingTeam.pipe(
      distinctUntilChanged()).subscribe((value: any) => {
        const lowerTerm = value.toLowerCase().trim();
        if (!value) {
          this.availableTeams.map((team: any) => team.searched = true);
          return;
        }
        this.availableTeams.map((team: any) => {
          team.searched = true;
          if (!(team.name.toLowerCase().includes(lowerTerm))) {
            team.searched = false;
          }
        });
      });
  }

  ngOnInit(): void {
    this.selectedRadioValue = this.radioButtons[1];
    this.teamsPlaceholder = this.translate.instant('to_sort_by_teams');
    this.selectAllTeams = true;
  }

  ngOnDestroy(): void {
    this.teardownQuickActionsObserver();
    this.onViewsDragEnd();
  }

  onViewsDragStart(event: MouseEvent) {
    if (event.button !== 0 || !this.viewsMainContainer) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('.view-settings-menu-container, .view-creator-menu-container, .view-share-confirmation-modal, input')) {
      return;
    }
    this.viewsDragging = true;
    this.viewsDragMoved = false;
    this.viewsDragStartX = event.pageX;
    this.viewsScrollStart = this.viewsMainContainer.nativeElement.scrollLeft;
  }

  private onViewsDragMove(event: MouseEvent) {
    if (!this.viewsDragging || !this.viewsMainContainer) {
      return;
    }
    const walk = event.pageX - this.viewsDragStartX;
    if (Math.abs(walk) > 5) {
      this.viewsDragMoved = true;
    }
    this.viewsMainContainer.nativeElement.scrollLeft = this.viewsScrollStart - walk;
  }

  private onViewsDragEnd() {
    this.viewsDragging = false;
    if (this.viewsDragMoved) {
      setTimeout(() => {
        this.viewsDragMoved = false;
      });
    }
  }

  onViewsScroll() {
    this.closeViewMenus();
  }

  private closeViewMenus() {
    if (this.selectedView) {
      this.selectedView.openSettings = false;
      this.selectedView.openCreatorView = false;
    }
    this.viewMenuPosition = null;
  }

  private setViewMenuPosition(event: MouseEvent, menuWidth: number) {
    const trigger = (event.currentTarget as HTMLElement)?.closest('.view-container') as HTMLElement
      || (event.target as HTMLElement).closest('.view-container') as HTMLElement;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    let left = rect.right - 42;
    const top = rect.bottom + 8;
    const maxLeft = window.innerWidth - menuWidth - 16;
    if (left > maxLeft) {
      left = Math.max(16, maxLeft);
    }
    if (left < 16) {
      left = 16;
    }
    this.viewMenuPosition = { top: top + 'px', left: left + 'px' };
  }

  private wasViewsDrag(): boolean {
    if (!this.viewsDragMoved) {
      return false;
    }
    this.viewsDragMoved = false;
    return true;
  }

  onViewClick(view: any, event: MouseEvent) {
    event.stopPropagation();
    if (this.wasViewsDrag() || this.selectedView === view) {
      return;
    }
    this.selectView(view);
  }

  onViewSettingsClick(view: any, event: MouseEvent) {
    event.stopPropagation();
    if (this.wasViewsDrag()) {
      return;
    }
    this.openViewSettingsMenu(view, event);
  }

  onViewCreatorClick(view: any, event: MouseEvent) {
    event.stopPropagation();
    if (this.wasViewsDrag()) {
      return;
    }
    this.openViewCreatorMenu(view, event);
  }

  private getUserRoles() {
    if (this.teams.length) {
      if (this.data.userRole.role.workspacePriority > 10) {
        this.teams.forEach((team: any) => {
          team.userHasAccess = true;
        });
      } else {
        this.teams.forEach((team: any) => {
          if (this.data.userRole.teams.filter((teamToFilter: any) => team.teamUuid === teamToFilter.team.uuid).length) {
            const role = this.data.userRole.teams.filter((teamToFilter: any) => team.teamUuid === teamToFilter.team.uuid)[0].team.myRole;
            team.userHasAccess = role.teamPriority >= 20;
          }
        });
      }
    }
  }

  highlightTeamAndDoQuickActions(team: any) {
    if (this.highlightedTeam && this.highlightedTeam !== team) {
      this.highlightedTeam.showQuickActions = false;
    }
    this.highlightedTeam = team;
    this.highlightedTeam.showQuickActions = true;
    if (!this.highlightedTeam.gotMembers) {
      this.quickActionsOpened.emit(this.highlightedTeam);
    }
  }

  closeTeamQuickActions() {
    this.highlightedTeam.showQuickActions = false;
    this.teardownQuickActionsObserver();
  }

  setMemberToDelete(member: any, event: MouseEvent) {
    this.memberToDelete = member;
    this.openConfirmationModal('deleteMember', event);
  }

  openConfirmationModal(modeToOpen: string, event: MouseEvent) {
    event.stopPropagation();
    document.body.style.overflow = 'hidden';
    this.pauseLoopModalOpen = modeToOpen === 'pauseLoop';
    this.deleteMemberModalOpen = modeToOpen === 'deleteMember';
    this.confirmationModalOpen = true;
  }

  pauseLoopModal() {
    this.pauseLoopEvent.emit(this.highlightedTeam);
    this.pauseLoopModalOpen = false;
    this.confirmationModalOpen = false;
  }

  removeMember(removeFrom: string, event: MouseEvent) {
    event.stopPropagation();
    this.removeMemberEvent.emit({type: removeFrom, member: this.memberToDelete, team: this.highlightedTeam});
    this.closeConfirmationModal();
  }

  closeConfirmationModal() {
    this.confirmationModalOpen = false;
    this.pauseLoopModalOpen = false;
    this.deleteMemberModalOpen = false;
    document.body.style.overflow = '';
  }

  gotToTeamDashboard() {
    this.goToTeamDashboard.emit(this.highlightedTeam.teamUuid);
  }

  onTeamCheckChange(teamUuid: string | string[], event: any) {
    const teamsToDeliver = Array.isArray(teamUuid) ? teamUuid : [teamUuid];
    const uuids = new Set(teamsToDeliver);
    this.availableTeams.forEach((team: any) => {
      if (uuids.has(team.uuid)) {
        team.checked = event.target.checked;
      }
    });
    this.teamFilterCheck.emit({teamUuids: teamsToDeliver, checked: event.target.checked});
    if (this.openAggregated && this.openAggregatedOnce) {
      this.aggregatedHasValue = false;
      this.getAggregatedScores.emit('');
    }
    this.teamsPlaceholder = this.getTeamsCheckState;
  }

  isTeamChecked(teamUuid: string) {
    return this.teams.find((t: any) => t.teamUuid === teamUuid)?.checked ?? false;
  }

  openScoreFilterContainer() {
    this.openFilterByScore = true;
    this.openFilterByTeams = false;
    if (this.showShareViewModal) {
      this.closeShareViewModal();
    }
  }

  openTeamFilterContainer() {
    this.openFilterByTeams = true;
    this.openFilterByScore = false;
    if (this.showShareViewModal) {
      this.closeShareViewModal();
    }
  }

  setFilterByScore() {
    this.openFilterByScore = false;
    this.setFilterForScore.emit(this.selectedRadioValue?.value);
  }

  seeWhy(teamUuid: any) {
    this.goToTeamDashboard.emit(teamUuid);
  }

  diselectAllTeams(event: any) {
    const checked = event;
    this.selectAllTeams = checked.target.checked;
    const teamUuids = this.availableTeams.map((team: any) => team.uuid);
    this.onTeamCheckChange(teamUuids, checked);
  }

  get getTeamsCheckState(): string | null {
    const total = this.availableTeams.length;
    const checkedCount = this.availableTeams.filter((team: any) => team.checked).length;
    // const isModified = this.teams.some((team:any, index:number) => {
    //   const originalItem = this.defaultTeams.find((item: any) => item.teamUuid === team.teamUuid);
    //   return originalItem ? originalItem.checked !== team.checked : true;
    // });
    const hasSelectedView = this.selectedView !== null;
    return (hasSelectedView) ? `${checkedCount}/${total} teams` : this.translate.instant('to_sort_by_teams');
  }

  createNewView() {
    this.viewSavePending = true;
    this.createNewViewEvent.emit({title:'New View',newView: true,byScore: this.selectedRadioValue.value});
    if (this.selectedView) this.selectedView.openSettings = false;
    this.saveViewButton = true;
  }

  saveViewChanges() {
    this.saveViewChangesEvent.emit(true);
  }

  searchMember(event: any) {
    this.searchingMember.next(event);
    this.memberToShareView = null;
    if (event) {
      this.showMembersLoading = true;
    }
  }

  searchTeam(event: any) {
    this.searchingTeam.next(event);
  }

  selectView(view: any) {
    if ((!view && this.selectedView) || (view && this.selectedView && this.selectedView !== view)) {
      this.selectedView.openSettings = false;
      this.selectedView.openCreatorView = false;
    }
    if (view) {
      this.selectedRadioValue = this.radioButtons[view.byScore === 'ASC' ? 1:0];
      this.selectTeamsByView(view);
    } else if (!view) {
      this.selectAllTeams = true;
      this.selectedRadioValue = this.radioButtons[1];
    }
    this.viewSavePending = !!view;
    this.saveViewButton = !!view && view.creatorPermission;
    this.selectedView = view;
    this.showDeleteViewConfirmation = false;
    this.viewToDelete = null;
    if (this.showShareViewModal) {
      this.closeShareViewModal();
    }
    this.openViewEvent.emit(view);
    if (view === null) {
      this.teamsPlaceholder = this.translate.instant('to_sort_by_teams');
    } else {
      this.teamsPlaceholder = this.getTeamsCheckState;
    }
  }

  openViewSettingsMenu(view: any, event?: MouseEvent) {
    this.selectView(view);
    view.openSettings = true;
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.openSettings = false;
    }
    this.tempViewTitle = view.viewTitle;
    if (event) {
      this.setViewMenuPosition(event, 312);
    }
  }

  openViewCreatorMenu(view: any, event?: MouseEvent) {
    this.selectView(view);
    view.openCreatorView = true;
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.openCreatorView = false;
    }
    if (event) {
      this.setViewMenuPosition(event, 280);
    }
  }

  changeSelectedViewTitle(view: any) {
    if (this.selectedView.viewTitle !== this.tempViewTitle) {
      view.viewTitle = this.tempViewTitle;
      this.patchViewEvent.emit({view: view, body:{name: this.tempViewTitle}});
      this.tempViewTitle = null;
    }
  }

  deleteView(viewToDelete: any) {
    this.deleteViewEvent.emit(viewToDelete.viewUuid);
    this.selectView(null);
    this.teamsPlaceholder = this.translate.instant('to_sort_by_teams');
  }

  openDeleteViewConfirmation(view: any) {
    this.viewToDelete = view;
    this.showDeleteViewConfirmation = true;
  }

  confirmDeleteView(confirmation: string) {
    if (confirmation === 'yes' && this.viewToDelete) {
      this.deleteView(this.viewToDelete);
    }
    this.showDeleteViewConfirmation = false;
    this.viewToDelete = null;
  }

  openShareViewModal(event: MouseEvent) {
    event.stopPropagation();
    if (!this.selectedView) {
      return;
    }
    if (this.showShareViewModal) {
      this.closeShareViewModal();
      return;
    }
    if (this.selectedView.openSettings) {
      this.selectedView.openSettings = false;
      this.viewMenuPosition = null;
    }
    this.openFilterByScore = false;
    this.openFilterByTeams = false;
    this.memberToSearch = null;
    this.memberToShareView = null;
    this.memberSearchedToShareView = [];
    this.searchingMember.next('');
    this.setShareMenuPosition(event);
    this.showShareViewModal = true;
  }

  closeShareViewModal() {
    this.showShareViewModal = false;
    this.shareMenuPosition = null;
    this.memberToSearch = null;
    this.memberToShareView = null;
    this.memberSearchedToShareView = [];
    this.searchingMember.next('');
  }

  private setShareMenuPosition(event: MouseEvent) {
    const trigger = (event.currentTarget as HTMLElement)
      || (event.target as HTMLElement).closest('.share-view-btn-container') as HTMLElement;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 312;
    let left = rect.right - menuWidth;
    const top = rect.bottom + 8;
    const maxLeft = window.innerWidth - menuWidth - 16;
    if (left > maxLeft) {
      left = Math.max(16, maxLeft);
    }
    if (left < 16) {
      left = 16;
    }
    this.shareMenuPosition = { top: top + 'px', left: left + 'px' };
  }

  getShareableMembers(): any[] {
    const sharedUuids = new Set(
      (this.selectedView?.sharedUsers || []).map((shared: any) => shared.user?.uuid)
    );
    return (this.memberSearchedToShareView || []).filter((member: any) => {
      const uuid = member?.user?.uuid;
      return uuid && !sharedUuids.has(uuid);
    });
  }

  getShareMemberName(member: any): string {
    const user = member?.user || member || {};
    const fullName = `${user.detail?.firstname || ''} ${user.detail?.lastname || ''}`.trim();
    return fullName || user.email || '';
  }

  getShareMemberEmail(member: any): string {
    const user = member?.user || member || {};
    return user.email || '';
  }

  getShareMemberAvatar(member: any): string | null {
    const path = (member?.user || member)?.avatar?.path;
    if (!path || !this.data.baseUrl) {
      return null;
    }
    return this.data.baseUrl + path;
  }

  getShareMemberInitials(member: any): string {
    const user = member?.user || member || {};
    const first = (user.detail?.firstname || '').charAt(0);
    const last = (user.detail?.lastname || '').charAt(0);
    const initials = `${first}${last}`.toUpperCase();
    if (initials) {
      return initials;
    }
    return (user.email || '').slice(0, 2).toUpperCase();
  }

  getShareMemberInitialsColor(member: any): string {
    const key = this.getShareMemberName(member) || this.getShareMemberEmail(member);
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    return SHARE_INITIALS_COLORS[Math.abs(hash) % SHARE_INITIALS_COLORS.length];
  }

  shareViewWithUser(member: any) {
    this.memberToShareView = member?.user ? member : { user: member };
  }

  shareViewConfirmation(confirmation: any) {
    if (confirmation === 'yes') {
      this.shareViewWithMemberEvent.emit({view: this.selectedView, member: this.memberToShareView});
      this.memberToSearch = null;
    }
    this.memberToShareView = null;
  }

  removeMemberFromView(memberToRemove: any, view: any) {
    this.removeMemberFromViewEvent.emit({memberUuid: memberToRemove.uuid, view: view});
  }

  private repositionQuickActions(triggerEl: HTMLElement, team: any) {
    const panel = triggerEl.querySelector('.team-quick-actions') as HTMLElement;
    if (!panel) return;

    const cardRect = triggerEl.getBoundingClientRect();
    const panelHeight = panel.offsetHeight;
    const borderTop = parseFloat(getComputedStyle(triggerEl).borderTopWidth) || 0;

    const centeredTop = cardRect.top + cardRect.height / 2 - panelHeight / 2;
    const maxTop = window.innerHeight - panelHeight - QUICK_ACTIONS_MARGIN;
    const viewportTop = Math.min(
      Math.max(centeredTop, QUICK_ACTIONS_MARGIN),
      Math.max(maxTop, QUICK_ACTIONS_MARGIN)
    );
    team.menuTop = viewportTop - cardRect.top - borderTop;
    team.shouldOpenLeft = (window.innerWidth - cardRect.right) < (panel.offsetWidth + QUICK_ACTIONS_SIDE_GUTTER);
    this.cdr.detectChanges();
  }

  private observeQuickActions(triggerEl: HTMLElement, team: any) {
    const panel = triggerEl.querySelector('.team-quick-actions') as HTMLElement;
    if (!panel || typeof ResizeObserver === 'undefined') return;
    this.quickActionsObserver = new ResizeObserver(() => this.repositionQuickActions(triggerEl, team));
    this.quickActionsObserver.observe(panel);
  }

  private teardownQuickActionsObserver() {
    this.quickActionsObserver?.disconnect();
    this.quickActionsObserver = undefined;
  }

  showSidePanel(triggerEl: HTMLElement, team: any) {
    team.menuTop = 0;
    this.teardownQuickActionsObserver();
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.repositionQuickActions(triggerEl, team);
      this.observeQuickActions(triggerEl, team);
    });
  }

  openQuickActionsMenu(triggerEl: HTMLElement, team: any) {
    if (team.showQuickActions) return;
    this.highlightTeamAndDoQuickActions(team);
    this.showSidePanel(triggerEl, team);
  }

  openAggregatedReports() {
    if (!this.openAggregatedOnce) this.getAggregatedScores.emit('');
    this.openAggregatedOnce = true;
    this.openAggregated = !this.openAggregated;
  }

  getPollingTimeRemaining(endDate: any) {
    if (endDate !== null) {
      const today = new Date();
      const expireDate = new Date(endDate.slice(5, 7) + '/' + endDate.slice(8, 10) + '/' + endDate.slice(0, 4));
      return Math.floor((Date.UTC(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate()) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / (1000 * 60 * 60 * 24));
    } else {
      return 0
    }
  }

  getRgba(hex: string, alpha: number = 1): string {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) throw new Error('Hex code invalid');

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private selectTeamsByView(view: any): void {
    this.teams.forEach((team: any) => {
      team.checked = false;
    });
    this.availableTeams.forEach((team: any) => {
      team.checked = false;
    });
    const viewTeamUuids = new Set(view?.byTeams ?? []);
    this.teams.forEach((team: any) => {
      team.checked = viewTeamUuids.has(team.teamUuid);
    });
    this.availableTeams.forEach((team: any) => {
      team.checked = viewTeamUuids.has(team.uuid);
    });
    
    this.selectAllTeams = this.availableTeams.length > 0 &&
      this.availableTeams.every((team: any) => team.checked);
  }
}
